import { z } from 'zod';

/**
 * Plan tiers + entitlement limits (NM-135, docs/45, docs/43 FR-43.11).
 *
 * The mechanism that makes the NM-1 pricing safe: the cost lever is **custom
 * (unshared) topics**, so each plan caps how many custom subjects an account may
 * subscribe to. Catalog subjects are shared across their subscribers (one check
 * serves everyone — NM-126/127), so they cost little per person and are generous
 * or unlimited on the paid tiers.
 *
 * Owner decisions (NM-135 feedback):
 *  - **A config module** (this file), the same shape as `cadence.ts` / rate limits
 *    — one edit point, env-overridable, no admin-editable runtime UI yet.
 *  - Past a cap, `POST /api/topics` originally **hard-rejected** with a 402; as of
 *    NM-198/200 it instead adds the subject **disabled** (paused — no fetches, no
 *    articles) and the client surfaces it in the Disabled group. The cap now meters
 *    how many subjects an account may keep **enabled** at once, per kind.
 *  - **A `catalog` flag** distinguishes custom from catalog subjects; each kind has
 *    its own cap, counting **enabled** subscriptions.
 *  - **Default new accounts to `trial`.**
 *  - The high-priority "faster freshness" idea was dropped (NM-150) — cadence was
 *    subscriber-count-driven and not a per-plan lever. **NM-271 reversed that**:
 *    every plan now carries a `checkIntervalMs` — the fastest cadence its
 *    subscribers are entitled to (a *floor* under the subscriber-count tiers, see
 *    `cadence.ts`). The owner's brief was "check much more often — 3–4×/day
 *    minimum, gated by plan": stale news is what makes people stop opening the
 *    app, and the closed-app push tickle (NM-251) only has something to say when
 *    a check has actually run.
 *
 * `plan` on an account is stored leniently (a string, resolved here) rather than a
 * strict enum column, for the reason focus/category are: an enum on read can
 * condemn a stored row and take account reads down. `resolvePlan` folds anything
 * unknown to the default, so a stray value degrades to trial instead of throwing.
 */

export const PLAN_IDS = ['trial', 'standard', 'pro', 'business'] as const;
export type PlanId = (typeof PLAN_IDS)[number];

/** Zod enum for the write boundary (the admin plan control), where a plan *is* constrained. */
export const PlanSchema = z.enum(PLAN_IDS);

/** New accounts start here (owner: "default to trial"). */
export const DEFAULT_PLAN: PlanId = 'trial';

/** The entitlement limits + price for one tier. All numbers are configurable (NM-135). */
export interface PlanLimits {
  /** Human label for admin/marketing surfaces. */
  label: string;
  /** Monthly price in USD (0 for the free trial). The pricing page reads these. */
  priceUsd: number;
  /** Max **custom** (non-catalog) subject subscriptions. The profitability cap. */
  customTopicCap: number;
  /** Max **catalog** (shared) subject subscriptions; `null` = unlimited. */
  catalogSubCap: number | null;
  /**
   * The fastest cadence this plan entitles its subscribers to (NM-271): a subject
   * is checked at least this often while someone on the plan actively follows it.
   * A floor under the subscriber-count tiers (`cadence.ts`), never a ceiling — a
   * popular subject can still run faster on its tier.
   */
  checkIntervalMs: number;
}

const HOUR_MS = 60 * 60 * 1000;

/**
 * The default tiers (NM-1, confirmed NM-135). Custom caps are the cost guarantee;
 * catalog is generous/unlimited on paid tiers. `trial` is the free starter every
 * new account gets — deliberately low custom cap so an unpaid account cannot run
 * up cost. NM-135 (`loadPlans`) lets every number be overridden from the
 * environment.
 */
export const DEFAULT_PLANS: Record<PlanId, PlanLimits> = {
  // Cadence (NM-271, retuned NM-277): the owner's floor is 3–4 checks a day for
  // everyone; the paid tiers buy a little fresher. Trial 8h (3×/day) · Standard 6h
  // (4×/day) · Pro 5h · Business 5h. NM-271 shipped pro 2h / business hourly and
  // beta's bill went from ~$0.33 to ~$10.72 a day (5 of 8 accounts on business →
  // ~44 subjects hourly, at ~$0.010 a check); the owner chose 5h for both paid
  // tiers, with account dormancy (NM-278, `cadence.ts`) as the larger lever. These
  // are the *plan* floors — a subject with 100+ subscribers is hourly on its tier
  // whatever its subscribers pay.
  trial: { label: 'Trial', priceUsd: 0, customTopicCap: 2, catalogSubCap: 10, checkIntervalMs: 8 * HOUR_MS },
  standard: { label: 'Standard', priceUsd: 9.99, customTopicCap: 5, catalogSubCap: 25, checkIntervalMs: 6 * HOUR_MS },
  pro: { label: 'Pro', priceUsd: 19.99, customTopicCap: 20, catalogSubCap: null, checkIntervalMs: 5 * HOUR_MS },
  business: {
    label: 'Business',
    priceUsd: 99.99,
    customTopicCap: 100,
    catalogSubCap: null,
    checkIntervalMs: 5 * HOUR_MS,
  },
};

/**
 * The scheduler ticks once a minute (`scheduler.ts`), so a plan interval below
 * that could not be honoured and would only make "next due" lie.
 */
export const MIN_PLAN_CHECK_INTERVAL_MS = 60 * 1000;

// `checkIntervalMs` is optional in the *override* (NM-271): an operator's
// `NEWSMONGER_PLANS` written before cadence became a plan lever must keep working,
// and a whole-table fallback to the defaults over one missing key would silently
// discard their caps and prices. A missing interval takes the tier's default.
const PlanLimitsSchema = z.object({
  label: z.string().min(1),
  priceUsd: z.number().min(0),
  customTopicCap: z.number().int().min(0),
  catalogSubCap: z.number().int().min(0).nullable(),
  checkIntervalMs: z.number().int().min(MIN_PLAN_CHECK_INTERVAL_MS).optional(),
});
const PlansSchema = z.object({
  trial: PlanLimitsSchema,
  standard: PlanLimitsSchema,
  pro: PlanLimitsSchema,
  business: PlanLimitsSchema,
});

function withDefaultIntervals(parsed: z.infer<typeof PlansSchema>): Record<PlanId, PlanLimits> {
  const tier = (id: PlanId): PlanLimits => ({
    ...parsed[id],
    checkIntervalMs: parsed[id].checkIntervalMs ?? DEFAULT_PLANS[id].checkIntervalMs,
  });
  return { trial: tier('trial'), standard: tier('standard'), pro: tier('pro'), business: tier('business') };
}

/**
 * Resolve the plan table, honouring a `NEWSMONGER_PLANS` JSON override (NM-135 —
 * "make sure the precise limits and costs are all configurable"). A malformed or
 * absent override falls back to `DEFAULT_PLANS` rather than throwing: a bad value
 * in the environment must not stop the app booting.
 */
export function loadPlans(env: NodeJS.ProcessEnv = process.env): Record<PlanId, PlanLimits> {
  const raw = env['NEWSMONGER_PLANS'];
  if (raw === undefined || raw.trim() === '') return DEFAULT_PLANS;
  try {
    const parsed = PlansSchema.safeParse(JSON.parse(raw));
    return parsed.success ? withDefaultIntervals(parsed.data) : DEFAULT_PLANS;
  } catch {
    return DEFAULT_PLANS;
  }
}

/** Fold a stored plan value (string, null, or unknown) to a known `PlanId`, defaulting to trial. */
export function resolvePlan(raw: string | null | undefined): PlanId {
  const parsed = PlanSchema.safeParse(raw);
  return parsed.success ? parsed.data : DEFAULT_PLAN;
}

/** The limits for an account's (possibly stray) plan value. */
export function planLimits(
  raw: string | null | undefined,
  plans: Record<PlanId, PlanLimits> = DEFAULT_PLANS,
): PlanLimits {
  return plans[resolvePlan(raw)];
}

/**
 * The fastest check interval any of `planValues` entitles (NM-271) — the plan
 * floor for a subject followed by accounts on those plans. Each value is a stored
 * (possibly stray) plan, resolved leniently like everywhere else. No subscribers
 * → `Infinity`: nobody is entitled to anything, so the floor imposes nothing and
 * the subscriber-count tier alone decides (which for 0 is also never).
 */
export function fastestPlanIntervalMs(
  planValues: readonly (string | null | undefined)[],
  plans: Record<PlanId, PlanLimits> = DEFAULT_PLANS,
): number {
  let fastest = Infinity;
  for (const raw of planValues) {
    const interval = planLimits(raw, plans).checkIntervalMs;
    if (interval < fastest) fastest = interval;
  }
  return fastest;
}

/**
 * "Checked hourly" / "Checked every 6 hours" / "Checked every 90 minutes" — the
 * marketing/checkout line for a plan's cadence, derived from the config so the
 * copy can never promise a cadence the scheduler does not run (NM-271, the same
 * reason the pricing page reads caps from `DEFAULT_PLANS`).
 */
export function cadenceLine(intervalMs: number): string {
  if (intervalMs <= HOUR_MS) return 'Checked hourly';
  if (intervalMs % HOUR_MS === 0) return `Checked every ${String(intervalMs / HOUR_MS)} hours`;
  return `Checked every ${String(Math.round(intervalMs / 60000))} minutes`;
}
